import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, Send } from 'lucide-react';
import { createSunMedInvoice, updateSunMedInvoice, updateSunMedInvoiceStatus } from '../utils/api';
import { getApiBaseUrl } from '../utils/apiBase';
import axios from 'axios';

const API_BASE = getApiBaseUrl();

const SunMedInvoiceForm = ({ invoice, onClose, onSave }) => {
  const isEditMode = !!invoice;
  const isSent = invoice?.status === 'sent';

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    dateIssued: new Date().toISOString().split('T')[0],
    billToName: 'Sun Med Medical Center',
    billToAddress: '1987 NW 88 Court, Suite 101 Doral, Florida 33172',
    billToEmail: 'sjamil@sunmedint.net',
    patientName: '',
    patientAddress: '',
    patientPhone: '',
    appointmentDate: '',
    appointmentTime: '',
    appointmentDestination: 'Bahamas Retina and Eye Care Services, Sandyport, Nassau, The Bahamas',
    lineItems: [
      { label: 'Pickup Transport', description: 'Transport to medical facility', amount: 0 },
      { label: 'Medical Wait Time', description: 'Wait time during appointment', amount: 0 },
      { label: 'Return Transport', description: 'Return transport from facility', amount: 0 }
    ],
    total: 0,
    notes: '',
    linkedBookingId: null
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoice) {
      setFormData({
        invoiceNumber: invoice.invoiceNumber || '',
        dateIssued: invoice.dateIssued || new Date().toISOString().split('T')[0],
        billToName: invoice.billToName || 'Sun Med Medical Center',
        billToAddress: invoice.billToAddress || 'Nassau, The Bahamas',
        billToEmail: invoice.billToEmail || '',
        patientName: invoice.patientName || '',
        patientAddress: invoice.patientAddress || '',
        patientPhone: invoice.patientPhone || '',
        appointmentDate: invoice.appointmentDate || '',
        appointmentTime: invoice.appointmentTime || '',
        appointmentDestination: invoice.appointmentDestination || '',
        lineItems: invoice.lineItems || [
          { label: 'Pickup Transport', description: 'Transport to medical facility', amount: 0 },
          { label: 'Medical Wait Time', description: 'Wait time during appointment', amount: 0 },
          { label: 'Return Transport', description: 'Return transport from facility', amount: 0 }
        ],
        total: invoice.total || 0,
        notes: invoice.notes || '',
        linkedBookingId: invoice.linkedBookingId || null
      });
    }
  }, [invoice]);

  // On create mode only: auto-fill next suggested invoice number
  useEffect(() => {
    if (isEditMode) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/invoices/sunmed/next-number`, { withCredentials: true });
        const suggested = res?.data?.suggested;
        if (!cancelled && suggested) {
          setFormData((prev) => (prev.invoiceNumber ? prev : { ...prev, invoiceNumber: suggested }));
        }
      } catch (err) {
        // Non-blocking: admin can still type a number manually
        console.warn('Failed to fetch next Sun Med invoice number:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isEditMode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...formData.lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'amount' ? parseFloat(value) || 0 : value
    };
    setFormData((prev) => ({
      ...prev,
      lineItems: updatedItems
    }));
  };

  const addLineItem = () => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        { label: '', description: '', amount: 0 }
      ]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.lineItems.length <= 1) {
      alert('At least one line item is required');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const calculateSubtotal = () => {
    return formData.lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const validateForm = () => {
    if (!formData.invoiceNumber.trim()) {
      alert('Invoice number is required');
      return false;
    }
    if (!formData.billToEmail.trim()) {
      alert('Recipient email is required');
      return false;
    }
    if (!formData.patientName.trim()) {
      alert('Patient name is required');
      return false;
    }
    if (!formData.appointmentDate) {
      alert('Appointment date is required');
      return false;
    }
    if (!formData.appointmentTime) {
      alert('Appointment time is required');
      return false;
    }
    if (!formData.appointmentDestination.trim()) {
      alert('Appointment destination is required');
      return false;
    }
    if (formData.lineItems.some((item) => !item.label.trim())) {
      alert('All line items must have a label');
      return false;
    }
    if (formData.total <= 0) {
      alert('Total amount must be greater than 0');
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const payload = {
        ...formData,
        lineItems: formData.lineItems.map((item) => ({
          label: item.label,
          description: item.description,
          amount: parseFloat(item.amount) || 0
        }))
      };

      if (isEditMode) {
        await updateSunMedInvoice(invoice.id, payload);
        alert('Invoice updated successfully');
      } else {
        await createSunMedInvoice(payload);
        alert('Invoice saved as draft');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save invoice:', error);
      alert(`Failed to save invoice: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReady = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const payload = {
        ...formData,
        lineItems: formData.lineItems.map((item) => ({
          label: item.label,
          description: item.description,
          amount: parseFloat(item.amount) || 0
        }))
      };

      let invoiceId;
      if (isEditMode) {
        await updateSunMedInvoice(invoice.id, payload);
        invoiceId = invoice.id;
      } else {
        const created = await createSunMedInvoice(payload);
        invoiceId = created.id;
      }

      // Update status to ready
      await updateSunMedInvoiceStatus(invoiceId, 'ready');
      alert('Invoice marked as ready to send');
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to mark invoice as ready:', error);
      alert(`Failed to mark ready: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const subtotal = calculateSubtotal();

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-4xl w-full my-8 border-2 border-cyan-500/30 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 sticky top-0 bg-gradient-to-br from-gray-900 to-gray-800 pb-4 z-10">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">
              {isEditMode ? 'Edit Invoice' : 'Create New Invoice'}
            </h2>
            {isSent && (
              <p className="text-red-400 text-sm font-semibold">⚠️ This invoice has been sent and is read-only</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition"
          >
            <X className="w-8 h-8" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Invoice Details */}
          <div className="bg-gray-800/50 p-6 rounded-xl border border-cyan-500/20">
            <h3 className="text-xl font-bold text-cyan-400 mb-4">Invoice Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Invoice Number *</label>
                <input
                  type="text"
                  value={formData.invoiceNumber}
                  onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="INV-2024-001"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Date Issued *</label>
                <input
                  type="date"
                  value={formData.dateIssued}
                  onChange={(e) => handleInputChange('dateIssued', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="bg-gray-800/50 p-6 rounded-xl border border-cyan-500/20">
            <h3 className="text-xl font-bold text-cyan-400 mb-4">Bill To (Recipient)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Organization Name</label>
                <input
                  type="text"
                  value={formData.billToName}
                  onChange={(e) => handleInputChange('billToName', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Address</label>
                <input
                  type="text"
                  value={formData.billToAddress}
                  onChange={(e) => handleInputChange('billToAddress', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-2">Recipient Email *</label>
                <input
                  type="email"
                  value={formData.billToEmail}
                  onChange={(e) => handleInputChange('billToEmail', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="sjamil@sunmedint.net"
                />
              </div>
            </div>
          </div>

          {/* Patient Information */}
          <div className="bg-gray-800/50 p-6 rounded-xl border border-cyan-500/20">
            <h3 className="text-xl font-bold text-cyan-400 mb-4">Patient Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Patient Name *</label>
                <input
                  type="text"
                  value={formData.patientName}
                  onChange={(e) => handleInputChange('patientName', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Patient Phone</label>
                <input
                  type="tel"
                  value={formData.patientPhone}
                  onChange={(e) => handleInputChange('patientPhone', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-2">Patient Address</label>
                <input
                  type="text"
                  value={formData.patientAddress}
                  onChange={(e) => handleInputChange('patientAddress', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Appointment Details */}
          <div className="bg-gray-800/50 p-6 rounded-xl border border-cyan-500/20">
            <h3 className="text-xl font-bold text-cyan-400 mb-4">Appointment Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Appointment Date *</label>
                <input
                  type="date"
                  value={formData.appointmentDate}
                  onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Appointment Time *</label>
                <input
                  type="time"
                  value={formData.appointmentTime}
                  onChange={(e) => handleInputChange('appointmentTime', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-2">Destination *</label>
                <input
                  type="text"
                  value={formData.appointmentDestination}
                  onChange={(e) => handleInputChange('appointmentDestination', e.target.value)}
                  disabled={isSent}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Medical facility name and address"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-gray-800/50 p-6 rounded-xl border border-cyan-500/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-cyan-400">Charges</h3>
              {!isSent && (
                <button
                  onClick={addLineItem}
                  className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 border border-cyan-500/30 transition text-sm flex items-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Item</span>
                </button>
              )}
            </div>
            <div className="space-y-4">
              {formData.lineItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-3 items-start">
                  <div className="col-span-12 md:col-span-4">
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => handleLineItemChange(index, 'label', e.target.value)}
                      disabled={isSent}
                      className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Item label"
                    />
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      disabled={isSent}
                      className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Description"
                    />
                  </div>
                  <div className="col-span-10 md:col-span-2">
                    <input
                      type="number"
                      step="0.01"
                      value={item.amount}
                      onChange={(e) => handleLineItemChange(index, 'amount', e.target.value)}
                      disabled={isSent}
                      className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="0.00"
                    />
                  </div>
                  {!isSent && formData.lineItems.length > 1 && (
                    <div className="col-span-2 md:col-span-1">
                      <button
                        onClick={() => removeLineItem(index)}
                        className="w-full px-2 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 border border-red-600/30 transition"
                      >
                        <Trash2 className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="mt-6 pt-4 border-t border-cyan-500/20">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-400">Subtotal (Auto-calculated):</span>
                <span className="text-white font-semibold">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <label className="text-gray-400">Total Amount *:</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.total}
                  onChange={(e) => handleInputChange('total', parseFloat(e.target.value) || 0)}
                  disabled={isSent}
                  className="w-32 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white font-bold text-right focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              {Math.abs(subtotal - formData.total) > 0.01 && (
                <p className="text-yellow-400 text-xs mt-2">
                  ⚠️ Total differs from subtotal by ${Math.abs(subtotal - formData.total).toFixed(2)}
                </p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-gray-800/50 p-6 rounded-xl border border-cyan-500/20">
            <h3 className="text-xl font-bold text-cyan-400 mb-4">Notes</h3>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              disabled={isSent}
              rows={4}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Additional notes or special instructions..."
            />
          </div>
        </div>

        {/* Action Buttons */}
        {!isSent && (
          <div className="mt-8 flex justify-end gap-3 pt-6 border-t border-cyan-500/20 sticky bottom-0 bg-gradient-to-br from-gray-900 to-gray-800">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition font-semibold"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              className="px-6 py-3 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-lg hover:bg-yellow-500/30 transition font-semibold flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              <span>{saving ? 'Saving...' : 'Save Draft'}</span>
            </button>
            <button
              onClick={handleMarkReady}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition font-semibold flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
              <span>{saving ? 'Processing...' : 'Mark Ready'}</span>
            </button>
          </div>
        )}

        {isSent && (
          <div className="mt-8 flex justify-end pt-6 border-t border-cyan-500/20">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition font-semibold"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SunMedInvoiceForm;
